import LoadingButton from "components/Common/LoadingButton"
import React, { useEffect, useState, useMemo } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Button,
} from "reactstrap"
import { axiosbaseurl } from "configure"
import { setUpdatedData } from "helpers/data_setters"
import { usePermissionData } from "AppContext"
import Select from "react-select"
import { customStyles } from "helpers/select-styles"
import swal from "sweetalert"
import Loader from "components/Common/Loader"

// Days of Week (unchanged)
const daysOfWeek = [
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
  { value: "Sunday", label: "Sunday" },
]

// Static reports (unchanged)
const crmReports = [
  { value: "Employee Daily Report", label: "Employee Daily Report" },
  {
    value: "Employee Designation Report",
    label: "Employee Designation Report",
  },
  { value: "Latest Task Report", label: "Latest Task Report" },
  { value: "Urgent Attention Report", label: "Urgent Attention Report" },
  { value: "ACTR Missing Task Report", label: "ACTR Missing Task Report" },
]

// Generate time options in 5-minute increments (00:00 to 23:55)
const generateTimeOptions = () => {
  const options = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 5) {
      const hour = h.toString().padStart(2, "0")
      const minute = m.toString().padStart(2, "0")
      const time = `${hour}:${minute}`
      options.push({ value: time, label: time })
    }
  }
  return options
}

const timeOptions = generateTimeOptions()

const SetAutomatedEmail = ({
  show,
  setShow,
  settable_data,
  template_id,
  settemplate_id,
}) => {
  const { checkAccess } = usePermissionData()
  const module_id = 24

  const isReadOnly =
    template_id &&
    checkAccess(module_id, "view") &&
    !checkAccess(module_id, "edit")

  // State
  const [employees, setEmployees] = useState([])
  const [selectedReports, setSelectedReports] = useState([])
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const [selectedTime, setSelectedTime] = useState({
    value: "09:00",
    label: "09:00",
  }) // default
  const [selectedDays, setSelectedDays] = useState([])
  const [loading, setLoading] = useState(false)

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const empRes = await axiosbaseurl.get("/employee-master/active")
        setEmployees(
          empRes.data?.data?.map(emp => ({
            value: emp.id,
            label: `${emp.name} ${emp.lastName || ""}`.trim(),
          })) || []
        )
      } catch (error) {
        console.error("Error fetching employees:", error)
      }
    }
    if (show) fetchEmployees()
  }, [show])

  // Load existing data when editing/viewing
  useEffect(() => {
    if (template_id && show) {
      const fetchTemplate = async () => {
        try {
          const response = await axiosbaseurl.get(
            `/automated/email/reports/${template_id}`
          )

          if (response.data.success) {
            const data = response.data.data

            setSelectedReports(
              crmReports.filter(r => data.reports?.includes(r.value)) || []
            )
            setSelectedEmployees(
              employees.filter(emp => data.employees?.includes(emp.value)) || []
            )
            // Find matching time option
            const timeValue = data.time || "09:00"
            const matchingTime = timeOptions.find(
              opt => opt.value === timeValue
            ) || {
              value: timeValue,
              label: timeValue,
            }
            setSelectedTime(matchingTime)
            setSelectedDays(
              daysOfWeek.filter(day => data.days?.includes(day.value)) || []
            )
          }
        } catch (error) {
          console.error(error)
          swal("", "Failed to load report configuration", "error")
        }
      }
      fetchTemplate()
    } else {
      // Reset for new
      setSelectedReports([])
      setSelectedEmployees([])
      setSelectedTime({ value: "09:00", label: "09:00" })
      setSelectedDays([])
    }
  }, [template_id, show, employees])

  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault()

    if (selectedReports.length === 0) {
      swal("", "Please select at least one report", "warning")
      return
    }
    if (selectedEmployees.length === 0) {
      swal("", "Please select at least one employee", "warning")
      return
    }
    if (selectedDays.length === 0) {
      swal("", "Please select at least one day", "warning")
      return
    }

    try {
      setLoading(true)

      const payload = {
        reports: selectedReports.map(r => r.value),
        employees: selectedEmployees.map(emp => emp.value),
        time: selectedTime.value,
        days: selectedDays.map(day => day.value),
      }

      let response
      if (template_id) {
        response = await axiosbaseurl.put(
          `/automated/email/reports/${template_id}`,
          payload
        )
      } else {
        response = await axiosbaseurl.post("/automated/email/reports", payload)
      }

      if (response.data.success) {
        settable_data(prev =>
          setUpdatedData(prev, response.data.data, template_id ? "edit" : "add")
        )
        setShow(false)
        swal(
          "",
          `Automated report ${
            template_id ? "updated" : "created"
          } successfully!`,
          "success"
        )
      }
    } catch (error) {
      console.error(error)
      const msg = error.response?.data?.message || "Something went wrong"
      swal("", msg, "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      size="xl"
      fullscreen="xl"
      isOpen={show}
      toggle={() => setShow(!show)}
      centered
    >
      <form onSubmit={handleSubmit} className="modal-content">
        <ModalHeader className="bg-primary text-white border-0">
          <div className="d-flex align-items-center justify-content-between w-100 px-3">
            <h5 className="mb-0">
              {template_id ? (isReadOnly ? "View" : "Edit") : "Schedule"}{" "}
              Automated Report Email
            </h5>
            <div>
              {!isReadOnly && (
                <Button type="submit" className="btn btn-success bg-green me-3">
                  <i className="fa fa-save me-1"></i>{" "}
                  <span>{template_id ? "Update" : "Submit"}</span>
                </Button>
              )}
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setShow(false)}
              >
                <i className="fa fa-times me-2"></i> Close
              </button>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="px-5 py-4">
          {loading && (
            <div className="text-center my-5">
              <Loader />
            </div>
          )}

          {!loading && (
            <Row>
              {/* Days */}
              <Col md={10} className="mb-4">
                <Label className="form-label fw-bold">
                  Days of Week <span className="text-danger">*</span>
                </Label>
                <Select
                  isMulti
                  value={selectedDays}
                  onChange={setSelectedDays}
                  options={daysOfWeek}
                  placeholder="Select days..."
                  styles={customStyles}
                  isDisabled={isReadOnly}
                  closeMenuOnSelect={false}
                />
              </Col>

              {/* Time Picker - 5 minute steps */}
              <Col md={2} className="mb-4">
                <Label className="form-label fw-bold">
                  Scheduled Time <span className="text-danger">*</span>
                </Label>
                <Select
                  value={selectedTime}
                  onChange={setSelectedTime}
                  options={timeOptions}
                  placeholder="Select time..."
                  styles={customStyles}
                  isDisabled={isReadOnly}
                  isSearchable
                  menuPlacement="auto"
                />
              </Col>

              {/* Reports */}
              <Col md={12} className="mb-4">
                <Label className="form-label fw-bold">
                  Select Reports <span className="text-danger">*</span>
                </Label>
                <Select
                  isMulti
                  value={selectedReports}
                  onChange={setSelectedReports}
                  options={crmReports}
                  placeholder="Select one or more reports..."
                  styles={customStyles}
                  isDisabled={isReadOnly}
                  closeMenuOnSelect={false}
                />
              </Col>

              {/* Employees */}
              <Col md={12} className="mb-4">
                <Label className="form-label fw-bold">
                  Recipients (Employees) <span className="text-danger">*</span>
                </Label>
                <Select
                  isMulti
                  value={selectedEmployees}
                  onChange={setSelectedEmployees}
                  options={employees}
                  placeholder="Select employees..."
                  styles={customStyles}
                  isDisabled={isReadOnly}
                  closeMenuOnSelect={false}
                />
              </Col>
            </Row>
          )}
        </ModalBody>
      </form>
    </Modal>
  )
}

export default SetAutomatedEmail
